import { Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';

/**
 * Primitivos de layout da página de apólice PME. Ficam colocalizados na pasta
 * do produto de propósito: o mockup tem tipografia e espaçamentos próprios
 * (rótulo em negrito com valor cinza, campo somente-leitura com cara de input
 * desabilitado) que não existem nos primitivos das outras telas.
 */

/** Par rótulo/valor das seções de leitura (grid de 4 colunas do mockup). */
export function PmeInfoRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <div className='flex min-w-0 flex-col gap-1'>
      <Text className='text-sm font-bold text-alba-black-500'>{label}</Text>
      <Text className='text-sm wrap-break-word text-alba-gray-600'>
        {value || '—'}
      </Text>
    </div>
  );
}

/**
 * Campo dos "Dados Básicos": o mockup desenha um input, mas a apólice é
 * leitura — então é um bloco com a aparência de campo desabilitado, sem
 * `<input>` de verdade (nada aqui é editável ou submetido).
 */
export function PmeReadOnlyField({
  label,
  value,
  required = false,
  trailingIcon,
  className,
}: {
  label: string;
  value?: string | null;
  required?: boolean;
  trailingIcon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className ?? ''}`}>
      <Text className='text-sm font-bold text-alba-black-500'>
        {label}
        {required ? <span className='text-alba-error-red-500'>*</span> : null}
      </Text>
      <div className='flex min-h-12 items-center justify-between gap-2 rounded-lg bg-alba-gray-200 px-4 py-3'>
        <Text className='truncate text-sm text-alba-gray-600'>
          {value || '—'}
        </Text>
        {trailingIcon ? (
          <span className='shrink-0 text-alba-gray-600'>{trailingIcon}</span>
        ) : null}
      </div>
    </div>
  );
}

/** Card colapsável de primeiro nível (Dados da Emissão, Dados do Risco, …). */
export function PmeAccordionSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className='rounded-2xl border border-alba-gray-200/80 bg-white shadow-[0px_4px_24px_0px_rgba(15,22,30,0.08)]'>
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className='flex min-h-18 w-full cursor-pointer items-center justify-between rounded-t-2xl border-none bg-transparent px-5 py-4 text-left sm:px-8'
      >
        <Text className='text-base font-semibold text-alba-black-500 sm:text-sm sm:font-medium sm:text-alba-gray-700'>
          {title}
        </Text>
        {isOpen ? (
          <ChevronUp className='h-4 w-4 shrink-0 text-alba-gray-700' />
        ) : (
          <ChevronDown className='h-4 w-4 shrink-0 text-alba-gray-700' />
        )}
      </button>
      {isOpen ? <div className='px-5 pb-6 sm:px-8'>{children}</div> : null}
    </div>
  );
}
